import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AudioPlayer from "components/Chat/AudioPlayer";
import React, { useDeferredValue, useEffect, useState } from "react";
import { Message } from "common/types";
import { defaultColorPallete } from "utils";
import { formatMessage, MessageMoreInfo } from "./utils";
import { ConversationTypeEnum, MessageTypeEnum } from "common/enums";
import FileDownloader from "components/Chat/FileDownloader";
import ImageViewer from "components/Chat/ImageViewer";
import VideoPlayer from "components/Chat/VideoPlayer";
import { useChatStore } from "store/useChatStore";
import { useShallow } from "zustand/shallow";
import { useInView } from "react-intersection-observer";
import { downloadMessageMedia } from "requests/tanstack/useDownloadMessageMedia";
import ImageGallery, { Imageprops } from "components/Chat/ImageGallery";

type Props = {
  messages: Message[];
  messagesLoading: boolean;
  fetchNextPage: () => void;
  // fetchPreviousPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  // hasPreviousPage: boolean;
  // isFetchingPreviousPage: boolean;
};

export default function ChatMessages(props: Props) {
  const {
    messages,
    fetchNextPage,
    isFetchingNextPage,
    messagesLoading,
    hasNextPage,
  } = props;

  const [selectedImageKey, setSelectedImageKey] = useState<string | null>(null);
  const deferredMessages = useDeferredValue(messages);
  const [downloadedImages, setDownloadedImages] = useState<
    Record<string, Imageprops>
  >({});

  useEffect(() => {
    const loadImages = async () => {
      const imagesMap = { ...downloadedImages };
      const newMessages = deferredMessages.filter(
        (message) =>
          message.messageType === MessageTypeEnum.image &&
          !imagesMap[message.id]
      );

      for (const message of newMessages) {
        const { url } = (message?.mediaUrl && await downloadMessageMedia(message.mediaUrl)) || {url: null};
        if (url) {
          const newImage = {
            src: url,
            key: message.id,
            caption: `${message.id} Lorem Ipsum is simply dummy text of the printing and typesetting industry.`,
            receivedAt: message.receivedAt,
          };
          imagesMap[message.id] = newImage;
          setDownloadedImages({ ...imagesMap });
        }
      }
    };

    loadImages();
  }, [deferredMessages, downloadedImages, messagesLoading]);
  const deferredDownloadedImages = useDeferredValue(downloadedImages);

  const { selectedConversation } = useChatStore(
    useShallow((state) => ({
      selectedConversation: state.selectedConversation,
    }))
  );

  const { ref: bottomRef, inView: bottomInView } = useInView();

  useEffect(() => {
    if (
      bottomInView &&
      hasNextPage &&
      !isFetchingNextPage &&
      !messagesLoading
    ) {
      fetchNextPage();
    }
  }, [bottomInView, messagesLoading, fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <>
      {deferredDownloadedImages && selectedImageKey !== null && (
        <ImageGallery
          selectedConversation={selectedConversation}
          images={deferredDownloadedImages}
          _selectedImageKey={selectedImageKey}
          onClose={() => setSelectedImageKey(null)}
        />
      )}

      {deferredMessages.map((message, index) => {
        const writerName = message?.contact?.name;
        return (
          <React.Fragment key={`OpenedChat-${message.id}-chatMessageFragment`}>
            {index === deferredMessages.length - 1 && hasNextPage && (
              <div
                key={`ConversationMessages-bottomSentinel-${deferredMessages.length}`}
                ref={bottomRef}
                style={{ height: 1 }}
              />
            )}
            {message.fromMe ? (
              <Box
                key={`OpenedChat-${message.id}-messageSenderContainer`}
                width="100%"
                display="flex"
                justifyContent="flex-end"
              >
                <Box
                  key={`OpenedChat-${message.id}-messageSenderContent`}
                  bgcolor="#BCE1E7"
                  minWidth="70px"
                  width="auto"
                  maxWidth="60%"
                  minHeight="30px"
                  borderRadius={"10px 10px 0px 10px"}
                  p={1}
                  sx={{ wordBreak: "break-word" }}
                >
                  {message.messageType === MessageTypeEnum.audio && (
                    <AudioPlayer
                      audioKey={`OpenedChat-${message.id}-messageSenderAudioMessage`}
                      audioUrl={message?.mediaUrl}
                    />
                  )}
                  {message.messageType === MessageTypeEnum.text && (
                    <Typography
                      color={defaultColorPallete.primary.dark}
                      fontSize="0.85rem"
                      key={`OpenedChat-${message.id}-messageSenderTextMessage`}
                    >
                      {formatMessage(message.textContent)}
                    </Typography>
                  )}
                  {message.messageType === MessageTypeEnum.image && (
                    <div onClick={() => setSelectedImageKey(message.id)}>
                      <ImageViewer
                        key={"image" + message.id}
                        imageUrl={deferredDownloadedImages[message.id]?.src}
                      />
                    </div>
                  )}
                  {message.messageType === MessageTypeEnum.video && (
                    <VideoPlayer videoUrl={message?.mediaUrl} />
                  )}
                  {message.messageType === MessageTypeEnum.document && (
                    <FileDownloader fileUrl={message?.mediaUrl} />
                  )}
                  <MessageMoreInfo message={message} />
                </Box>
              </Box>
            ) : (
              <Box
                key={`OpenedChat-${message.id}-messageRecipientContainer`}
                width="100%"
                display="flex"
                justifyContent="flex-start"
              >
                <Box
                  key={`OpenedChat-${message.id}-messageRecipientContent`}
                  bgcolor={defaultColorPallete.neutral.main}
                  minWidth="70px"
                  width="auto"
                  maxWidth="60%"
                  minHeight="30px"
                  borderRadius={"0px 10px 10px 10px"}
                  p={1}
                  sx={{ wordBreak: "break-word" }}
                >
                  {selectedConversation?.type === ConversationTypeEnum.GROUP &&
                    writerName && (
                      <Typography
                        color={defaultColorPallete.primary.main}
                        fontSize="0.7rem"
                      >
                        {writerName}
                      </Typography>
                    )}
                  {message.messageType === MessageTypeEnum.audio && (
                    <AudioPlayer
                      audioKey={`OpenedChat-${message.id}-messageRecipientAudioMessage`}
                      audioUrl={message?.mediaUrl}
                    />
                  )}
                  {message.messageType === MessageTypeEnum.text && (
                    <Typography
                      key={`OpenedChat-${message.id}-messageRecipientTextMessage`}
                      color="black"
                      fontSize="0.8rem"
                    >
                      {formatMessage(message.textContent)}
                    </Typography>
                  )}
                  {message.messageType === MessageTypeEnum.image && (
                    <div onClick={() => setSelectedImageKey(message.id)}>
                      <ImageViewer
                        key={"image" + message.id}
                        imageUrl={deferredDownloadedImages[message.id]?.src}
                      />
                    </div>
                  )}
                  {message.messageType === MessageTypeEnum.video && (
                    <VideoPlayer videoUrl={message?.mediaUrl} />
                  )}
                  {message.messageType === MessageTypeEnum.document && (
                    <FileDownloader fileUrl={message?.mediaUrl} />
                  )}
                  <MessageMoreInfo message={message} />
                </Box>
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
